import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@direct-campaign/ui";
import type { VoterListItem, VoterListResponse } from "@direct-campaign/types";
import { Search, Upload } from "lucide-react";
import { apiFetch } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const PAGE_SIZE = 25;

export function VotersPage() {
  const { user } = useAuth();
  const [votersList, setVotersList] = useState<VoterListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(PAGE_SIZE),
        });
        if (query.trim()) params.set("q", query.trim());
        const res = await apiFetch(`/api/voters?${params}`);
        if (res.ok) {
          const data = (await res.json()) as VoterListResponse;
          setVotersList(data.voters ?? []);
          setTotal(data.total ?? 0);
        }
      } catch {
        // keep whatever is currently shown
      } finally {
        setLoading(false);
      }
    }

    // Debounce so we don't fire a request per keystroke.
    const timer = setTimeout(() => void load(), 250);
    return () => clearTimeout(timer);
  }, [page, query]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Voters</h2>
          <p className="text-muted-foreground mt-1">
            {total > 0
              ? `${total.toLocaleString()} voter${total === 1 ? "" : "s"} in your campaign.`
              : "Your campaign's voter file."}
          </p>
        </div>
        {user?.role === "admin" && (
          <Button asChild>
            <Link to="/dashboard/voters/import">
              <Upload className="w-4 h-4" />
              Import voters
            </Link>
          </Button>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Search by name, address, or voter ID…"
          className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Name</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Address</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Party</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Precinct</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Voter ID</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : votersList.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                  {query
                    ? "No voters match your search."
                    : "No voters yet. Import a voter file CSV to get started."}
                </td>
              </tr>
            ) : (
              votersList.map((v) => (
                <tr key={v.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 font-medium">
                    {v.firstName} {v.lastName}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {v.address}, {v.city}, {v.state} {v.zip}
                  </td>
                  <td className="px-4 py-3">{v.party ?? "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{v.precinct ?? "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                    {v.voterCode}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
